// src/components/auth/ResetPasswordPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import authService from "../../api/authClient";
import "../../css/LoginPage.css"; // Reusing styles
import skillMatchLogo from "../../assets/images/login/skillMatchLogo.svg";
import { Link } from "react-router-dom"; // For navigation links

const ResetPasswordPage = () => {
  // For extracting token if it's a URL parameter like /reset-password/:token
  // const { token: tokenFromParams } = useParams();

  // For extracting token if it's a query parameter like /reset-password?token=YOUR_TOKEN
  const location = useLocation();
  const navigate = useNavigate();

  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenFromQuery = queryParams.get("token");
    if (tokenFromQuery) {
      setToken(tokenFromQuery);
    } else {
      setMessage("Invalid or missing password reset token in URL.");
      setIsError(true);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setMessage("Please enter and confirm your new password.");
      setIsError(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage("New passwords do not match.");
      setIsError(true);
      return;
    }
    if (newPassword.length < 8) {
      setMessage("Password must be at least 8 characters long.");
      setIsError(true);
      return;
    }
    if (!token) {
      setMessage(
        "Password reset token is missing. Please use the link from your email."
      );
      setIsError(true);
      return;
    }

    setIsLoading(true);
    setMessage("");
    setIsError(false);

    try {
      await authService.resetPassword(token, newPassword);
      setMessage(
        "Your password has been reset successfully! You can now log in with your new password."
      );
      setIsError(false);
      setNewPassword("");
      setConfirmPassword("");
      // Optionally redirect to login after a delay
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Reset password error:", error);
      setMessage(
        error.response?.data?.detail ||
          "Failed to reset password. The link may be invalid or expired."
      );
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-form-section" style={{ justifyContent: "center" }}>
        <div className="logo-container">
          <img
            src={skillMatchLogo}
            alt="SkillMatchAI Logo"
            className="logo-image"
          />
        </div>
        <div className="form-container">
          <h1 className="form-title">Reset Your Password</h1>
          <p className="form-subtitle" style={{ marginBottom: "20px" }}>
            Enter your new password below.
          </p>

          {message && (
            <div className={isError ? "error-message" : "success-message"}>
              {message}
            </div>
          )}

          {token ? ( // Only show form if token is present
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="newPassword">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  placeholder="Minimum 8 characters"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="confirmPassword">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  placeholder="••••••••••••••••••"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={isLoading}
                />
              </div>
              <button
                type="submit"
                className="signin-button"
                disabled={isLoading}
                style={{ marginTop: "20px" }}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          ) : (
            // Message if token is not found in URL (already handled by useEffect setting message)
            <div className="error-message" style={{ textAlign: "center" }}>
              If you have a password reset link, please ensure you've clicked it
              correctly.
            </div>
          )}
          <div className="signup-prompt" style={{ marginTop: "30px" }}>
            <Link to="/login" className="signup-link">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
